import { useQuery } from '@tanstack/react-query'
import axios from 'axios'

import { CacheKeys } from '../lib/cache'
import { PaginatedResults, toCamelCase } from '../lib/typeHelper'
import { User } from './user'

export enum EsignEnvelopeStatus {
  SENT = 'SENT',
  SIGNED = 'SIGNED',
  DECLINED = 'DECLINED',
  VOIDED = 'VOIDED',
}

export enum EsignEnvelopeVendor {
  DOCUSEAL = 'DOCUSEAL',
}

export enum EsignEnvelopeType {
  LOSS_RUN = 'LOSS_RUN',
  BOR_AUTHORIZATION = 'BOR_AUTHORIZATION',
  CANAL_BOR_AUTHORIZATION = 'CANAL_BOR_AUTHORIZATION',
  ROCKLAKE_PAYMENT_AUTHORIZATION = 'ROCKLAKE_PAYMENT_AUTHORIZATION',
  ROCKLAKE_CREDIT_CARD_AUTHORIZATION = 'ROCKLAKE_CREDIT_CARD_AUTHORIZATION',
}

export interface EsignEnvelope {
  id: string
  envelopeId: string
  dateCreated: string
  dateUpdated: string
  dateSent: string
  dateSigned: string
  status: EsignEnvelopeStatus
  recipient: User
  vendor: EsignEnvelopeVendor
  metadata: Record<string, any>
  type: EsignEnvelopeType
}

export interface EsignScreeningDisclosure {
  url: string
  clientId: string
  envelope: EsignEnvelope
}

export function getEnvelope(envelopeType: EsignEnvelopeType) {
  return axios
    .post(`/api/v1/insure/esign/envelope/`, { envelope_type: envelopeType })
    .then((response) => toCamelCase(response.data) as EsignScreeningDisclosure)
}

export function syncEnvelope(envelopeId: string) {
  return axios
    .post(`/api/v1/insure/esign/${envelopeId}/sync/`)
    .then((response) => toCamelCase(response.data) as EsignScreeningDisclosure)
}

export interface OrgEnvelope {
  id: string
  dateCreated: string
  dateSent: string | null
  dateSigned: string | null
  status: string
  type: string
  recipientEmail: string
  recipientName: string
}

function getOrgEnvelopes(orgId: string): Promise<PaginatedResults<OrgEnvelope>> {
  return axios
    .get(`/api/v1/insure/organisations/${orgId}/envelopes/`)
    .then((response) => toCamelCase(response.data) as PaginatedResults<OrgEnvelope>)
}

export function useOrgEnvelopes(orgId: string | undefined) {
  const { data, isLoading } = useQuery({
    queryKey: [CacheKeys.ORG_ENVELOPES, orgId],
    queryFn: () => getOrgEnvelopes(orgId!),
    enabled: !!orgId,
  })
  return { envelopes: data?.results ?? [], count: data?.count ?? 0, isLoading }
}

export interface EnvelopeSignUrl {
  signUrl: string
  clientId: string | null
}

function getEnvelopeSignUrl(envelopeId: string): Promise<EnvelopeSignUrl> {
  return axios
    .get(`/api/v1/insure/envelopes/${envelopeId}/sign-url/`)
    .then((response) => toCamelCase(response.data) as EnvelopeSignUrl)
}

export function useEnvelopeSignUrl(envelopeId: string | undefined) {
  return useQuery({
    queryKey: [CacheKeys.ENVELOPE_SIGN_URL, envelopeId],
    queryFn: () => getEnvelopeSignUrl(envelopeId!),
    enabled: !!envelopeId,
    retry: false,
  })
}

export interface EnvelopeSignLink {
  link: string
  recipientEmail: string
}

export function getEnvelopeSignLink(orgId: string, envelopeId: string): Promise<EnvelopeSignLink> {
  return axios
    .post(`/api/v1/insure/organisations/${orgId}/envelopes/${envelopeId}/sign-link/`)
    .then((response) => toCamelCase(response.data) as EnvelopeSignLink)
}
